import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, ArrowLeft, UserPlus, FileText, Upload, CreditCard, BadgeCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const STEPS = [
  { id: 1, name: "Compte parent", icon: UserPlus },
  { id: 2, name: "Informations enfant", icon: FileText },
  { id: 3, name: "Documents", icon: Upload },
  { id: 4, name: "Validation", icon: BadgeCheck },
];

const LEVELS = [
  "Maternelle - Petite Section", "Maternelle - Moyenne Section", "Maternelle - Grande Section",
  "Primaire - CP1", "Primaire - CP2", "Primaire - CE1", "Primaire - CE2", "Primaire - CM1", "Primaire - CM2",
  "Collège - 6ème", "Collège - 5ème", "Collège - 4ème", "Collège - 3ème",
  "Lycée - Seconde", "Lycée - Première", "Lycée - Terminale",
];

export default function Admissions() {
  const { user, register: registerUser, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(user ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [enrollmentId, setEnrollmentId] = useState(null);

  // Parent form
  const [parentData, setParentData] = useState({
    full_name: "", email: "", password: "", phone: "", otp_channel: "email",
  });
  // OTP de confirmation d'ouverture de compte
  const [otpState, setOtpState] = useState(null); // {pending_id, channel, destination_masked, dev_otp}
  const [otpCode, setOtpCode] = useState("");
  // Child form
  const [childData, setChildData] = useState({
    child_first_name: "", child_last_name: "", child_birth_date: "", child_gender: "M",
    requested_level: "", previous_school: "", medical_info: "", notes: "",
  });
  const [docTypes, setDocTypes] = useState([]);
  const [docFiles, setDocFiles] = useState({}); // {doc_type_id: File}

  // Anti-robot (captcha arithmétique)
  const [captcha, setCaptcha] = useState(null); // {captcha_id, question}
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const loadCaptcha = () => {
    api.get("/captcha").then(({ data }) => { setCaptcha(data); setCaptchaAnswer(""); }).catch(() => {});
  };
  React.useEffect(() => { if (step === 1 && !user) loadCaptcha(); }, [step]);

  React.useEffect(() => {
    if (step === 3 && docTypes.length === 0) {
      api.get("/dossiers/required-documents").then(r => setDocTypes(r.data)).catch(() => {});
    }
  }, [step]);

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const handleStep1 = async () => {
    if (!parentData.password || !parentData.full_name) {
      toast.error("Veuillez remplir tous les champs requis");
      return;
    }
    if (!parentData.email && !parentData.phone) {
      toast.error("Fournissez au moins un email ou un numéro de téléphone");
      return;
    }
    if (parentData.otp_channel === "sms" && !parentData.phone) {
      toast.error("Un numéro de téléphone est requis pour recevoir le code par SMS");
      return;
    }
    if (parentData.otp_channel === "email" && !parentData.email) {
      toast.error("Un email est requis pour recevoir le code par email");
      return;
    }
    if (!captchaAnswer.trim()) {
      toast.error("Veuillez répondre à la question anti-robot");
      return;
    }
    setLoading(true);
    try {
      const res = await registerUser({
        ...parentData, email: parentData.email || null,
        captcha_id: captcha?.captcha_id, captcha_answer: captchaAnswer.trim(),
      });
      if (res.require_otp) {
        setOtpState(res);
        if (res.dev_otp) toast.info(`Mode test — code : ${res.dev_otp}`);
        else toast.success(res.channel === "sms" ? "Code de confirmation envoyé par SMS !" : "Code de confirmation envoyé par email !");
      } else {
        toast.success("Compte parent créé avec succès !");
        setStep(2);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création du compte");
      loadCaptcha(); // nouveau défi après chaque tentative
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOtp = async () => {
    if (!otpCode || otpCode.length !== 6) return toast.error("Saisissez le code à 6 chiffres");
    setLoading(true);
    try {
      await verifyOtp(otpState.pending_id, otpCode);
      toast.success("Compte confirmé et créé avec succès !");
      setOtpState(null);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = () => {
    if (!childData.child_first_name || !childData.child_last_name || !childData.child_birth_date || !childData.requested_level) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setStep(3);
  };

  const handleStep3 = async () => {
    // Documents obligatoires définis par l'admin
    const missing = docTypes.filter(dt => dt.required && !docFiles[dt.id]);
    if (missing.length > 0) {
      toast.error(`Documents obligatoires manquants : ${missing.map(d => d.name).join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      const documents_files = [];
      for (const dt of docTypes) {
        const f = docFiles[dt.id];
        if (!f) continue;
        if (f.size > 8_000_000) {
          toast.error(`${dt.name} : fichier trop volumineux (max 8 Mo)`);
          setLoading(false);
          return;
        }
        documents_files.push({ doc_type_id: dt.id, doc_name: dt.name, file_data_b64: await fileToBase64(f) });
      }
      const docsList = documents_files.map(d => d.doc_name);
      const { data } = await api.post("/enrollments", { ...childData, documents: docsList, documents_files });
      setEnrollmentId(data.id);
      toast.success(`Dossier d'inscription créé${documents_files.length ? ` — ${documents_files.length} document(s) joint(s)` : ""} !`);
      setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la création du dossier");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen py-12" data-testid="page-admissions">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-10">
          <div className="chip mb-4 mx-auto inline-flex"><span>Admissions 2026 — 2027</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-[#1E3A8A] tracking-tight mb-3">
            Inscrivez votre enfant en 4 étapes.
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Création de compte, informations sur l&apos;enfant, téléversement des pièces et confirmation. Tout se fait en ligne, en moins de 10 minutes.
          </p>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-8" data-testid="admissions-stepper">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <React.Fragment key={s.id}>
                <div className="flex flex-col items-center gap-2 flex-1" data-testid={`step-${s.id}`}>
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
                    step > s.id ? "bg-[#166534] text-white" :
                    step === s.id ? "bg-[#1E3A8A] text-white ring-4 ring-[#1E3A8A]/15" :
                    "bg-slate-100 text-slate-400"
                  }`}>
                    {step > s.id ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
                  </div>
                  <span className={`text-xs font-semibold text-center hidden sm:block ${step >= s.id ? "text-[#1E3A8A]" : "text-slate-400"}`}>
                    {s.name}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 w-8 lg:w-16 -mt-6 ${step > s.id ? "bg-[#166534]" : "bg-slate-200"}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Forms */}
        <div className="bg-white rounded-2xl border border-slate-200 p-7 lg:p-10">
          {step === 1 && !otpState && (
            <div className="space-y-5" data-testid="step1-form">
              <h2 className="font-heading text-2xl font-bold text-[#1E3A8A] mb-2">Créez votre compte parent</h2>
              <p className="text-sm text-slate-600 mb-6">Ce compte vous permettra de suivre les inscriptions et la scolarité de vos enfants. Un code de confirmation vous sera envoyé par SMS ou email.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="p-name">Nom complet *</Label>
                  <Input id="p-name" data-testid="parent-name-input" value={parentData.full_name} onChange={(e) => setParentData({...parentData, full_name: e.target.value})} placeholder="M./Mme Diallo Ibrahim" />
                </div>
                <div>
                  <Label htmlFor="p-phone">Téléphone {parentData.otp_channel === "sms" ? "*" : ""}</Label>
                  <Input id="p-phone" data-testid="parent-phone-input" value={parentData.phone} onChange={(e) => setParentData({...parentData, phone: e.target.value})} placeholder="+224 622 00 00 00" />
                </div>
              </div>
              <div>
                <Label htmlFor="p-email">Email {parentData.otp_channel === "email" ? "*" : "(optionnel)"}</Label>
                <Input id="p-email" type="email" data-testid="parent-email-input" value={parentData.email} onChange={(e) => setParentData({...parentData, email: e.target.value})} placeholder="vous@example.com" />
              </div>
              <div>
                <Label>Comment souhaitez-vous recevoir votre code de confirmation ? *</Label>
                <div className="flex gap-3 mt-2">
                  <button type="button" onClick={() => setParentData({...parentData, otp_channel: "email"})}
                          className={`flex-1 p-3 rounded-xl border-2 text-sm font-semibold transition ${parentData.otp_channel === "email" ? "border-[#1E3A8A] bg-blue-50 text-[#1E3A8A]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                          data-testid="channel-email-btn">
                    ✉️ Par email
                  </button>
                  <button type="button" onClick={() => setParentData({...parentData, otp_channel: "sms"})}
                          className={`flex-1 p-3 rounded-xl border-2 text-sm font-semibold transition ${parentData.otp_channel === "sms" ? "border-[#EA580C] bg-orange-50 text-[#EA580C]" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                          data-testid="channel-sms-btn">
                    📱 Par SMS
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="p-pass">Mot de passe *</Label>
                <PasswordInput id="p-pass" data-testid="parent-password-input" value={parentData.password} onChange={(e) => setParentData({...parentData, password: e.target.value})} placeholder="Au moins 6 caractères" />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <Label htmlFor="p-captcha" className="text-amber-900">Vérification anti-robot : {captcha?.question || "chargement..."} *</Label>
                <Input id="p-captcha" type="number" inputMode="numeric" value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Votre réponse" className="mt-1 bg-white" data-testid="captcha-answer-input" />
              </div>
              <div className="flex justify-end pt-3">
                <Button onClick={handleStep1} disabled={loading} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="step1-next-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continuer <ArrowRight className="h-4 w-4" /></>}
                </Button>
              </div>
            </div>
          )}

          {step === 1 && otpState && (
            <div className="space-y-5 max-w-md mx-auto text-center" data-testid="step1-otp-form">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#166534] flex items-center justify-center">
                <Check className="h-7 w-7 text-white" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#1E3A8A]">Confirmez votre compte</h2>
              <p className="text-sm text-slate-600">
                Un code à 6 chiffres a été envoyé par <strong>{otpState.channel === "sms" ? "SMS" : "email"}</strong> à <strong>{otpState.destination_masked}</strong>.
              </p>
              {otpState.dev_otp && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="reg-dev-otp">
                  <strong>Mode test :</strong> votre code est <code className="font-mono font-bold text-base">{otpState.dev_otp}</code>
                </div>
              )}
              <Input
                type="text" inputMode="numeric" maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="•   •   •   •   •   •"
                className="text-center text-2xl tracking-[0.5em] font-bold h-14"
                data-testid="reg-otp-input"
              />
              <Button onClick={handleConfirmOtp} disabled={loading || otpCode.length !== 6} className="w-full bg-[#166534] hover:bg-[#14532d] text-white gap-2 py-6 disabled:opacity-50" data-testid="reg-otp-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer et continuer"}
              </Button>
              <button type="button" onClick={() => { setOtpState(null); setOtpCode(""); }} className="text-xs text-slate-500 hover:text-[#1E3A8A]" data-testid="reg-otp-back">
                ← Modifier mes informations
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="step2-form">
              <h2 className="font-heading text-2xl font-bold text-[#1E3A8A] mb-2">Informations sur l&apos;enfant</h2>
              <p className="text-sm text-slate-600 mb-6">Renseignez les informations de l&apos;élève à inscrire.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Prénom *</Label>
                  <Input data-testid="child-first-name" value={childData.child_first_name} onChange={(e) => setChildData({...childData, child_first_name: e.target.value})} />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input data-testid="child-last-name" value={childData.child_last_name} onChange={(e) => setChildData({...childData, child_last_name: e.target.value})} />
                </div>
                <div>
                  <Label>Date de naissance *</Label>
                  <Input type="date" data-testid="child-birth-date" value={childData.child_birth_date} onChange={(e) => setChildData({...childData, child_birth_date: e.target.value})} />
                </div>
                <div>
                  <Label>Sexe *</Label>
                  <Select value={childData.child_gender} onValueChange={(v) => setChildData({...childData, child_gender: v})}>
                    <SelectTrigger data-testid="child-gender"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Masculin</SelectItem>
                      <SelectItem value="F">Féminin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Niveau demandé *</Label>
                <Select value={childData.requested_level} onValueChange={(v) => setChildData({...childData, requested_level: v})}>
                  <SelectTrigger data-testid="child-level"><SelectValue placeholder="Choisir un niveau..." /></SelectTrigger>
                  <SelectContent>
                    {LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ancienne école</Label>
                <Input data-testid="child-prev-school" value={childData.previous_school} onChange={(e) => setChildData({...childData, previous_school: e.target.value})} placeholder="Optionnel" />
              </div>
              <div>
                <Label>Informations médicales</Label>
                <Textarea data-testid="child-medical" value={childData.medical_info} onChange={(e) => setChildData({...childData, medical_info: e.target.value})} placeholder="Allergies, traitements, particularités..." rows={3} />
              </div>
              <div className="flex justify-between pt-3">
                <Button variant="outline" onClick={() => setStep(1)} className="gap-2" data-testid="step2-back-btn">
                  <ArrowLeft className="h-4 w-4" /> Retour
                </Button>
                <Button onClick={handleStep2} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2" data-testid="step2-next-btn">
                  Continuer <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="step3-form">
              <h2 className="font-heading text-2xl font-bold text-[#1E3A8A] mb-2">Documents à fournir</h2>
              <p className="text-sm text-slate-600 mb-4">Joignez les documents demandés par l&apos;école (PDF ou photo, max 8 Mo par fichier). Les documents marqués <span className="text-red-600 font-bold">*</span> sont obligatoires.</p>
              <div className="space-y-3">
                {docTypes.map(dt => (
                  <div key={dt.id} className={`p-4 rounded-xl border transition-all ${docFiles[dt.id] ? "border-[#166534] bg-green-50" : dt.required ? "border-orange-300 bg-orange-50/40" : "border-slate-200 bg-white"}`} data-testid={`doc-upload-${dt.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-medium text-slate-800">
                        {dt.name} {dt.required && <span className="text-red-600 font-bold">*</span>}
                        {docFiles[dt.id] && <span className="ml-2 text-xs text-[#166534] font-semibold">✓ {docFiles[dt.id].name}</span>}
                      </div>
                      <input
                        type="file"
                        accept=".pdf,image/*"
                        onChange={(e) => setDocFiles({ ...docFiles, [dt.id]: e.target.files?.[0] || null })}
                        className="text-xs file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[#1E3A8A] file:text-white file:text-xs file:cursor-pointer cursor-pointer"
                        data-testid={`doc-file-${dt.id}`}
                      />
                    </div>
                  </div>
                ))}
                {docTypes.length === 0 && <div className="text-sm text-slate-400 text-center py-4">Chargement de la liste des documents...</div>}
              </div>
              <div className="flex justify-between pt-3">
                <Button variant="outline" onClick={() => setStep(2)} className="gap-2" data-testid="step3-back-btn">
                  <ArrowLeft className="h-4 w-4" /> Retour
                </Button>
                <Button onClick={handleStep3} disabled={loading} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="step3-submit-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Soumettre le dossier <ArrowRight className="h-4 w-4" /></>}
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-8" data-testid="step4-success">
              <div className="w-20 h-20 rounded-full bg-[#166534] text-white mx-auto mb-6 flex items-center justify-center">
                <BadgeCheck className="h-12 w-12" />
              </div>
              <h2 className="font-heading text-3xl font-extrabold text-[#1E3A8A] mb-3">Dossier soumis avec succès !</h2>
              <p className="text-slate-600 max-w-xl mx-auto mb-6">
                Votre demande d&apos;inscription a été enregistrée. Notre équipe administrative la traitera sous 48h et vous notifiera par email et SMS.
              </p>
              {enrollmentId && (
                <div className="inline-flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-lg text-sm mb-6">
                  <FileText className="h-4 w-4 text-[#1E3A8A]" />
                  <span className="font-mono text-slate-700">Référence: <strong>{enrollmentId.slice(0, 8).toUpperCase()}</strong></span>
                </div>
              )}
              <div className="flex flex-wrap justify-center gap-3">
                <Button onClick={() => navigate("/dashboard")} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="success-dashboard-btn">
                  Aller à mon espace
                </Button>
                <WhatsAppButton
                  message={`Bonjour, je viens de soumettre une demande d'inscription pour mon enfant. ${enrollmentId ? "Référence: " + enrollmentId.slice(0, 8).toUpperCase() : ""}`}
                  label="Confirmer via WhatsApp"
                  testId="success-whatsapp-btn"
                />
                <Button variant="outline" onClick={() => navigate("/")} data-testid="success-home-btn">
                  Retour à l&apos;accueil
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
